import type { BottomTabScreenProps } from '@react-navigation/bottom-tabs';
import type { LinkProps } from '@react-navigation/native';
import type { NativeStackScreenProps } from '@react-navigation/native-stack';

import type { AuthParam } from '@/features/authorization/navigation/authRoute';
import { AuthRoute } from '@/features/authorization/navigation/authRoute';
import type { ChatsParam } from '@/features/chats/navigation/chatsRoute';
import { ChatsRoute } from '@/features/chats/navigation/chatsRoute';
import type { ExperimentsParam } from '@/features/experiments/navigation/experimentsRoute';
import { ExperimentsRoute } from '@/features/experiments/navigation/experimentsRoute';
import type { FeatureManagementParam } from '@/features/feature-management/navigation/featureManagementRoute';
import { FeatureManagementRoute } from '@/features/feature-management/navigation/featureManagementRoute';
import type { RegionTab } from '@/features/geo-area/components/region/regionTypes';
import type { GeoAreaParam } from '@/features/geo-area/navigation/geoAreaRoute';
import { GeoAreaRoute } from '@/features/geo-area/navigation/geoAreaRoute';
import type { GroupsParam } from '@/features/groups/navigation/groupsRoute';
import { GroupsRoute } from '@/features/groups/navigation/groupsRoute';
import type { MediaRouteParam } from '@/features/media/navigation/mediaRoute';
import { MediaRoute } from '@/features/media/navigation/mediaRoute';
import type {
  MeinOrtChannel,
  MeinOrtTab
} from '@/features/mein-ort/components/meinOrtTypes';
import type { PostCreationParam } from '@/features/post-editor/navigation/postCreationRoute';
import { PostCreationRoute } from '@/features/post-editor/navigation/postCreationRoute';
import type { PostEditorParam } from '@/features/post-editor/navigation/postEditorRoute';
import { PostEditorRoute } from '@/features/post-editor/navigation/postEditorRoute';
import type { PostsParam } from '@/features/posts/navigation/postsRoute';
import { PostsRoute } from '@/features/posts/navigation/postsRoute';
import type { PrivacyParam } from '@/features/privacy/navigation/privacyRoute';
import { PrivacyRoute } from '@/features/privacy/navigation/privacyRoute';
import type { ProfileSettingParam } from '@/features/profile-setting/navigation/profileSettingRoute';
import { ProfileSettingRoute } from '@/features/profile-setting/navigation/profileSettingRoute';
import type { StartParam } from '@/features/start/navigation/startRoute';
import { StartRoute } from '@/features/start/navigation/startRoute';
import type { TutorialParam } from '@/features/tutorial/navigation/tutorialRoute';
import { TutorialRoute } from '@/features/tutorial/navigation/tutorialRoute';

export const TabRoute = {
  Start: 'start',
  Radio: 'radio',
  Region: 'region',
  Profile: 'profile'
} as const;

export type TabRoute = (typeof TabRoute)[keyof typeof TabRoute];

export type TabParam = {
  [TabRoute.Start]: undefined;
  [TabRoute.Radio]: { channel?: MeinOrtChannel; tab?: MeinOrtTab } | undefined;
  [TabRoute.Region]: { tab?: RegionTab } | undefined;
  [TabRoute.Profile]: undefined;
};

export const AppRoute = {
  Tabs: 'tabs',
  ...TabRoute,
  ...ProfileSettingRoute,
  ...GeoAreaRoute,
  ...AuthRoute,
  ...PrivacyRoute,
  ...PostsRoute,
  ...PostEditorRoute,
  ...PostCreationRoute,
  ...GroupsRoute,
  ...FeatureManagementRoute,
  ...ChatsRoute,
  ...StartRoute,
  ...ExperimentsRoute,
  ...MediaRoute,
  ...TutorialRoute
} as const;

export type AppRoute = (typeof AppRoute)[keyof typeof AppRoute];

export type ScreenParam =
  | undefined
  | {
      screen?: AppRoute;
      initial?: boolean;
      params?:
        | {
            screen?: AppRoute;
            initial?: boolean;
            params?: ScreenParam;
          }
        | TabParam[typeof TabRoute.Radio]
        | TabParam[typeof TabRoute.Region];
    };

export type AppParam = {
  [AppRoute.Tabs]: ScreenParam | undefined;
} & TabParam &
  ProfileSettingParam &
  GeoAreaParam &
  AuthParam &
  PrivacyParam &
  PostsParam &
  PostEditorParam &
  PostCreationParam &
  GroupsParam &
  FeatureManagementParam &
  ChatsParam &
  StartParam &
  ExperimentsParam &
  MediaRouteParam &
  TutorialParam;

export type AppNativeStackScreenProps<T extends AppRoute> =
  NativeStackScreenProps<AppParam, T>;

export type AppTabScreenProps<T extends AppRoute> = BottomTabScreenProps<
  AppParam,
  T
>;

export type NavigationItemProps = LinkProps<
  Partial<AppParam>,
  Partial<AppRoute>
>;
